import React, { useState, useEffect } from 'react';
import {
  ShieldAlert,
  X,
  AlertTriangle,
  ArrowRight,
  KeyRound,
  AlertOctagon,
  Loader2,
  Check,
  ShieldCheck,
  Lock
} from 'lucide-react';
import { useStepUpAuth } from '../hooks/useStepUpAuth';
import { SupervisorAuthData } from '../types/auth';

export type { SupervisorAuthData };

export interface RectifiedItemSummary {
  nombre: string;
  originalProyectoNombre?: string;
  proyectoNombre?: string;
}

export interface RampSupervisorAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  rectifiedItems: RectifiedItemSummary[];
  onAuthorized: (supervisor: SupervisorAuthData) => void | Promise<void>;
  initialSupervisors?: Array<{ id: string; name: string; puesto?: string; hasPin?: boolean }>;
}

export const RampSupervisorAuthModal: React.FC<RampSupervisorAuthModalProps> = ({
  isOpen,
  onClose,
  rectifiedItems,
  onAuthorized
}) => {
  const {
    hasActiveSupervisorSession,
    activeSupervisor,
    supervisorEmployees,
    loadingEmployees,
    isVerifying,
    error: authError,
    lockoutRemainingSeconds,
    authorizeWithActiveSession,
    verifySupervisorPin,
    clearError
  } = useStepUpAuth({
    requiredPermissions: ['Recepcion_Rampa', 'Superadmin'],
    actionName: 'Rectificación de destinos en Rampa'
  });

  const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>('');
  const [supervisorPinInput, setSupervisorPinInput] = useState<string>('');
  const [showPinDelegation, setShowPinDelegation] = useState<boolean>(false);
  const [isSubmittingSession, setIsSubmittingSession] = useState<boolean>(false);

  // Seleccionar supervisor por defecto al cargar catálogo
  useEffect(() => {
    if (supervisorEmployees.length > 0 && !selectedSupervisorId) {
      const defaultSup =
        supervisorEmployees.find((e) => e.name.toLowerCase().includes('mikel') && e.hasPin) ||
        supervisorEmployees.find((e) => e.hasPin) ||
        supervisorEmployees[0];
      if (defaultSup) {
        setSelectedSupervisorId(defaultSup.id);
      }
    }
  }, [supervisorEmployees, selectedSupervisorId]);

  // Reset de estados al abrir/cerrar modal
  useEffect(() => {
    if (isOpen) {
      setSupervisorPinInput('');
      clearError();
      setShowPinDelegation(!hasActiveSupervisorSession);
    }
  }, [isOpen, hasActiveSupervisorSession, clearError]);

  // Manejo de autorización expedita con Sesión Activa (Sudo Mode)
  const handleAuthorizeWithActiveSession = async () => {
    try {
      setIsSubmittingSession(true);
      const sup = authorizeWithActiveSession();
      await onAuthorized(sup);
    } catch (err) {
      console.error('Error autorizando con sesión activa:', err);
    } finally {
      setIsSubmittingSession(false);
    }
  };

  // Manejo de verificación por PIN
  const handleVerifySupervisorPinAndSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedSupervisorId) {
      return;
    }
    if (!supervisorPinInput || supervisorPinInput.trim().length < 4) {
      return;
    }

    const result = await verifySupervisorPin(selectedSupervisorId, supervisorPinInput);
    if (result.success && result.supervisor) {
      setSupervisorPinInput('');
      await onAuthorized(result.supervisor);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 no-print">
      <div className="bg-surfaceHigh border border-amber-500/40 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
        
        {/* Cabecera */}
        <div className="px-5 py-3.5 bg-amber-500/10 border-b border-amber-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                <span>Autorización de Supervisor Requerida</span>
              </h3>
              <p className="text-[11px] text-amber-300/90 font-mono">
                Rectificación de destinos / Fraccionamiento en Rampa
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-surface transition"
            aria-label="Cerrar modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-5 space-y-4">
          
          {/* Desviación detectada */}
          <div className="p-3 bg-surface border border-borderSubtle rounded-lg text-xs text-slate-300 space-y-1.5">
            <p className="font-semibold text-amber-300 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
              <span>Desviación detectada respecto a la OAB original ({rectifiedItems.length} ítem{rectifiedItems.length === 1 ? '' : 's'}):</span>
            </p>
            <div className="max-h-32 overflow-y-auto space-y-1.5 pr-1 font-mono text-[11px]">
              {rectifiedItems.map((ritem, rIdx) => {
                const orig = ritem.originalProyectoNombre || 'Stock General (MTS)';
                const curr = ritem.proyectoNombre || 'Stock General (MTS)';
                return (
                  <div
                    key={rIdx}
                    className="p-1.5 rounded bg-surfaceHigh border border-borderSubtle flex items-center justify-between gap-2"
                  >
                    <span className="text-slate-200 font-sans font-medium truncate">{ritem.nombre}</span>
                    <div className="flex items-center gap-1.5 text-[10px] shrink-0">
                      <span className="text-slate-400">{orig}</span>
                      <ArrowRight className="w-3 h-3 text-amber-400" />
                      <span className="text-emerald-300 font-bold">{curr}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sudo Mode: Sesión Activa con Permisos */}
          {hasActiveSupervisorSession && activeSupervisor && (
            <div className="p-3.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-emerald-300">
                      Sesión de Supervisor Detectada (Sudo Mode)
                    </p>
                    <p className="text-[11px] text-slate-300">
                      Usuario autenticado:{' '}
                      <strong className="text-white">{activeSupervisor.name}</strong>
                      {activeSupervisor.puestos?.length ? ` (${activeSupervisor.puestos.join(', ')})` : ''}
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAuthorizeWithActiveSession}
                disabled={isSubmittingSession}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg text-xs transition shadow-lg shadow-emerald-950/40 active:scale-98 disabled:opacity-50"
              >
                {isSubmittingSession ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Asentando con mi sesión...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Autorizar con mi sesión activa ({activeSupervisor.name.split(' ')[0]})</span>
                  </>
                )}
              </button>

              <div className="pt-1 flex items-center justify-between text-[11px]">
                <span className="text-slate-400">¿Operando para otro supervisor?</span>
                <button
                  type="button"
                  onClick={() => setShowPinDelegation((prev) => !prev)}
                  className="text-amber-400 hover:text-amber-300 underline font-medium"
                >
                  {showPinDelegation ? 'Ocultar ingreso de PIN' : 'Ingresar PIN de otro supervisor'}
                </button>
              </div>
            </div>
          )}

          {/* Delegación por PIN (Kiosco o Supervisor Alterno) */}
          {(showPinDelegation || !hasActiveSupervisorSession) && (
            <form onSubmit={handleVerifySupervisorPinAndSubmit} className="space-y-4 pt-1">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Supervisor que autoriza:
                </label>
                <select
                  value={selectedSupervisorId}
                  onChange={(e) => setSelectedSupervisorId(e.target.value)}
                  disabled={loadingEmployees || isVerifying}
                  className="w-full px-3 py-2 text-xs bg-surface border border-borderSubtle rounded-lg text-slate-100 focus:outline-none focus:border-amber-400 disabled:opacity-50"
                >
                  {loadingEmployees ? (
                    <option value="">Cargando catálogo de supervisores...</option>
                  ) : supervisorEmployees.length === 0 ? (
                    <option value="">No hay supervisores disponibles</option>
                  ) : (
                    supervisorEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} {emp.hasPin ? '🔑' : ''}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1 flex items-center justify-between">
                  <span>PIN de Autorización (4 dígitos):</span>
                  {lockoutRemainingSeconds ? (
                    <span className="text-[10px] text-red-400 font-mono font-bold flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Bloqueado ({lockoutRemainingSeconds}s)
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-500 font-mono">Confidencial</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    autoFocus={!hasActiveSupervisorSession}
                    placeholder="••••"
                    disabled={isVerifying || Boolean(lockoutRemainingSeconds)}
                    value={supervisorPinInput}
                    onChange={(e) => setSupervisorPinInput(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                    className="w-full text-center tracking-[0.5em] font-mono text-xl py-2 px-3 bg-surface border border-borderSubtle rounded-lg text-amber-300 focus:outline-none focus:border-amber-400 placeholder-slate-600 disabled:opacity-40"
                  />
                  <KeyRound className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              {authError && (
                <div className="p-2.5 rounded bg-red-500/10 border border-red-500/30 text-xs text-red-300 flex items-center gap-2">
                  <AlertOctagon className="w-4 h-4 text-red-400 shrink-0" />
                  <span>{authError}</span>
                </div>
              )}

              <div className="flex items-center justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-2 text-xs text-slate-400 hover:text-slate-200 transition rounded"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    isVerifying ||
                    supervisorPinInput.length < 4 ||
                    Boolean(lockoutRemainingSeconds)
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-lg text-xs transition shadow active:scale-95 disabled:opacity-50"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Verificando PIN...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Autorizar por PIN y Asentar</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};
